import * as Sentry from '@sentry/nextjs'

/**
 * 構造化 JSON ロガー
 *
 * 設計方針:
 *   - process.stdout.write でJSON 1行ずつ出力（console を使わず ESLint no-console と整合）
 *   - debug / info は stdout のみ（Vercel Runtime Logs に流れる）
 *   - warn は Sentry.captureMessage('warning') にも送る
 *   - error は context.error が Error なら captureException、無ければ captureMessage('error')
 *
 * 用途:
 *   - 既存の標準出力ベースのデバッグ出力を置き換える
 *   - エラー専用の logError() (lib/errors.ts) と相互補完。
 *     logger.error は構造化ログ + Sentry、logError は Sentry 専用ヘルパー。
 *
 * 設計: docs/observability-target-state.md G3 / docs/logger-migration.md
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export type LogContext = Record<string, unknown>

interface LogEntry extends LogContext {
  level: LogLevel
  message: string
  timestamp: string
}

function emitJson(entry: LogEntry): void {
  // 2026-07-17 (PR #9974 再レビュー): 本モジュールは client component からも import される
  // ('use client' の hook が既存で 3 件)。ブラウザに process.stdout は存在せず
  // (Next.js のブラウザ process polyfill は env のみ提供し stdout を持たない)、
  // 無防備に write を呼ぶと TypeError でレンダーごと落ちる。
  // stdout の無い環境では stdout 出力だけを諦め、Sentry 送出 (warn/error) は継続する。
  if (typeof process === 'undefined' || typeof process.stdout?.write !== 'function') return
  process.stdout.write(JSON.stringify(entry) + '\n')
}

function buildEntry(
  level: LogLevel,
  message: string,
  baseContext: LogContext,
  extraContext: LogContext = {}
): LogEntry {
  return {
    level,
    message,
    timestamp: new Date().toISOString(),
    ...baseContext,
    ...extraContext,
  }
}

function sendToSentry(level: LogLevel, message: string, context: LogContext): void {
  if (level === 'debug' || level === 'info') return

  if (level === 'warn') {
    Sentry.captureMessage(message, { level: 'warning', extra: context })
    return
  }

  // level === 'error'
  const err = context.error
  if (err instanceof Error) {
    Sentry.captureException(err, { extra: { message, ...context } })
  } else {
    Sentry.captureMessage(message, { level: 'error', extra: context })
  }
}

export interface Logger {
  debug: (message: string, context?: LogContext) => void
  info: (message: string, context?: LogContext) => void
  warn: (message: string, context?: LogContext) => void
  error: (message: string, context?: LogContext) => void
}

export function createLogger(baseContext: LogContext = {}): Logger {
  return {
    debug(message, context) {
      const entry = buildEntry('debug', message, baseContext, context)
      emitJson(entry)
    },
    info(message, context) {
      const entry = buildEntry('info', message, baseContext, context)
      emitJson(entry)
    },
    warn(message, context) {
      const entry = buildEntry('warn', message, baseContext, context)
      emitJson(entry)
      sendToSentry('warn', message, { ...baseContext, ...context })
    },
    error(message, context) {
      const entry = buildEntry('error', message, baseContext, context)
      emitJson(entry)
      sendToSentry('error', message, { ...baseContext, ...context })
    },
  }
}

/** デフォルトのトップレベル logger（追加 context なし） */
export const logger: Logger = createLogger()

/**
 * webhookの401拒否をSentryへ可視化する（2026-08-27・s08発見の型: 401はruntime error
 * として記録されずSLOに現れない。line-genba/route.tsで7日間401継続の実例あり）。
 */
export function warnWebhook401(route: string, reason: string): void {
  logger.warn(reason, { route })
}
